import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocket } from "@/contexts/WebSocketContext";

/**
 * Fetches and tracks online/offline status for a set of users.
 * Makes an initial REST call and re-polls every 30 seconds.
 * Automatically picks up the global WebSocket for real-time presence_update events.
 */
export function useOnlineStatus(
  userIds: string[],
  wsOverride?: WebSocket | null,
) {
  const { ws: globalWs } = useWebSocket();
  const ws = wsOverride ?? globalWs;
  const [statusMap, setStatusMap] = useState<Record<string, boolean>>({});
  const prevIdsRef = useRef<string>("");

  const fetchStatus = useCallback(async (ids: string[]) => {
    if (!ids.length) return;
    try {
      const res = await fetch("/api/social/online-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userIds: ids }),
      });
      if (res.ok) {
        const data: Record<string, boolean> = await res.json();
        setStatusMap(prev => ({ ...prev, ...data }));
      }
    } catch {}
  }, []);

  // Re-fetch when the userIds list changes
  useEffect(() => {
    const key = userIds.slice().sort().join(",");
    if (key === prevIdsRef.current) return;
    prevIdsRef.current = key;
    fetchStatus(userIds);
  }, [userIds, fetchStatus]);

  // Poll every 30 seconds
  useEffect(() => {
    if (!userIds.length) return;
    const interval = setInterval(() => fetchStatus(userIds), 30_000);
    return () => clearInterval(interval);
  }, [userIds, fetchStatus]);

  // Subscribe to presence_update events from the global WebSocket
  // Re-subscribe when ws changes (including when it first becomes available)
  useEffect(() => {
    if (!ws) return;

    const handler = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "presence_update" && data.userId) {
          setStatusMap(prev => ({ ...prev, [data.userId]: data.isOnline }));
        }
      } catch {}
    };

    // If already open, subscribe immediately
    if (ws.readyState === WebSocket.OPEN) {
      ws.addEventListener("message", handler);
      return () => ws.removeEventListener("message", handler);
    }

    // If still connecting, wait for open then subscribe
    const onOpen = () => ws.addEventListener("message", handler);
    ws.addEventListener("open", onOpen);
    return () => {
      ws.removeEventListener("open", onOpen);
      ws.removeEventListener("message", handler);
    };
  }, [ws]);

  const isOnline = useCallback(
    (userId: string) => statusMap[userId] ?? false,
    [statusMap],
  );

  return { isOnline, statusMap };
}
